// Client for the report generation flow (docs/api-integration.md): upload
// CSV files, follow the job, download the workbook. Requests go through this
// app's backend, which holds the reporting service's API key.
import { apiRequest, apiDownload, ApiError } from './apiClient';
import { downloadBlob } from './downloadUtils';

// Limits of the reporting service, used to catch problems before uploading.
export const REPORT_LIMITS = {
  maxFiles: 24,
  maxFileBytes: 20 * 1024 * 1024,
  maxTextLength: 120,
  requiredColumns: ['product', 'description', 'USD', 'category']
};

const UPLOAD_TIMEOUT_MS = 120 * 1000;
const STATUS_TIMEOUT_MS = 30 * 1000;
const POLL_INTERVAL_MS = 2000;
// A job the service lost is reported as failed after about three minutes.
const MAX_WAIT_MS = 6 * 60 * 1000;
const ACTIVE_STATUSES = ['queued', 'processing'];
const JOB_STATUSES = [...ACTIVE_STATUSES, 'succeeded', 'failed'];

export class ReportError extends Error {
  constructor(message, { code = 'UNKNOWN', status = 0, errors = [], requestId = null, retryAfter = null } = {}) {
    super(message);
    this.name = 'ReportError';
    this.code = code;
    this.status = status;
    this.errors = errors; // [{ code, message, file, ... }] as sent by the service
    this.requestId = requestId;
    this.retryAfter = retryAfter;
  }
}

// What to tell the user for each error code. The service's own message is
// used for validation errors, because it names the file and the column.
const MESSAGES = {
  REPORTS_NOT_CONFIGURED: 'Report generation is not set up on this server yet. Ask an administrator to configure the reporting service.',
  REPORT_SERVICE_NOT_CONFIGURED: 'The reporting service is not ready to accept reports yet. Ask an administrator to check its API keys.',
  REPORT_SERVICE_AUTH: 'The reporting service did not accept this dashboard\'s credentials. Ask an administrator to check the server settings.',
  REPORT_SERVICE_UNREACHABLE: 'The reporting service could not be reached. Try again in a moment.',
  REPORT_SERVICE_TIMEOUT: 'The reporting service took too long to respond. Try again in a moment.',
  INVALID_RESPONSE: 'The reporting service sent a response this app could not understand. Try again, and contact an administrator if it keeps happening.',
  RATE_LIMITED: 'Too many reports were requested in the last minute.',
  BUSY: 'The reporting service is busy generating other reports.',
  INTERNAL_ERROR: 'The reporting service could not generate the report. Please try again.',
  JOB_LOST: 'The reporting service restarted while your report was being generated. Generate it again.',
  REPORT_EXPIRED: 'This report has expired. Reports are kept for a limited time, so generate it again.',
  NOT_FOUND: 'This report is no longer available. Generate it again.',
  FILE_TOO_LARGE: 'One of the files is larger than the reporting service accepts (20 MB per file).',
  TOO_MANY_ROWS: 'The selected files contain more rows than the reporting service accepts in one report. Select fewer files.',
  TOO_MANY_FILES: 'Too many files were selected. A report can use up to 24 files.',
  TIMED_OUT: 'The report is taking much longer than expected. Try generating it again.',
  NETWORK: 'Unable to reach the dashboard server. Check your connection and try again.',
  UNKNOWN: 'Something went wrong while generating the report. Please try again.'
};

// Codes for which the service's own wording is more useful than ours.
const USE_SERVICE_MESSAGE = [
  'EMPTY_FILE', 'MISSING_COLUMN', 'UNPARSABLE_AMOUNT', 'EMPTY_AMOUNT', 'BLANK_PRODUCT',
  'UNSUPPORTED_FORMAT', 'NOT_CSV', 'BAD_ENCODING', 'BAD_REQUEST'
];

const messageFor = (entry) => {
  if (entry && entry.message && USE_SERVICE_MESSAGE.includes(entry.code)) return entry.message;
  return MESSAGES[entry && entry.code] || (entry && entry.message) || MESSAGES.UNKNOWN;
};

// Convert a failed backend call into a ReportError.
const toReportError = (err) => {
  if (err instanceof ReportError) return err;
  if (err instanceof ApiError) {
    if (err.kind === 'network') return new ReportError(MESSAGES.NETWORK, { code: 'NETWORK' });
    if (err.kind === 'timeout') return new ReportError(MESSAGES.REPORT_SERVICE_TIMEOUT, { code: 'REPORT_SERVICE_TIMEOUT' });
    const body = err.body || {};
    const errors = Array.isArray(body.errors) ? body.errors : [];
    const first = errors[0] || {};
    return new ReportError(messageFor(first.code ? first : { message: err.message }), {
      code: first.code || 'UNKNOWN',
      status: err.status,
      errors,
      requestId: body.request_id || first.request_id || null,
      retryAfter: typeof body.retry_after === 'number' ? body.retry_after : null
    });
  }
  return new ReportError(MESSAGES.UNKNOWN);
};

const assertJob = (job) => {
  if (!job || typeof job.job_id !== 'string' || !JOB_STATUSES.includes(job.status)) {
    throw new ReportError(MESSAGES.INVALID_RESPONSE, { code: 'INVALID_RESPONSE' });
  }
  if (job.status === 'succeeded' && (!job.report || typeof job.report.id !== 'string')) {
    throw new ReportError(MESSAGES.INVALID_RESPONSE, { code: 'INVALID_RESPONSE' });
  }
  return job;
};

// A job that finished as "failed" becomes an error, like any other failure.
const failedJobError = (job) => {
  const errors = Array.isArray(job.errors) ? job.errors : [];
  const first = errors[0] || { code: 'INTERNAL_ERROR' };
  return new ReportError(messageFor(first), {
    code: first.code || 'INTERNAL_ERROR',
    errors,
    requestId: first.request_id || job.request_id || null
  });
};

// Upload the CSV files. Resolves with the job the service created.
export const submitReport = async (files, { title = '', company = '' } = {}) => {
  const form = new FormData();
  files.forEach((file) => form.append('files', file, file.name));
  if (title.trim()) form.append('title', title.trim());
  if (company.trim()) form.append('company', company.trim());

  try {
    return assertJob(await apiRequest('/api/reports', { method: 'POST', body: form, timeoutMs: UPLOAD_TIMEOUT_MS }));
  } catch (err) {
    throw toReportError(err);
  }
};

export const getReportJob = async (jobId) => {
  try {
    return assertJob(
      await apiRequest(`/api/reports/jobs/${encodeURIComponent(jobId)}`, { timeoutMs: STATUS_TIMEOUT_MS })
    );
  } catch (err) {
    throw toReportError(err);
  }
};

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Follow a job until it finishes. Resolves with the succeeded job, rejects
// with a ReportError if it fails or takes too long. `isCancelled` lets the
// caller stop waiting; the promise then resolves with null.
export const waitForReport = async (
  job,
  { onUpdate, isCancelled = () => false, sleep = defaultSleep, intervalMs = POLL_INTERVAL_MS, maxWaitMs = MAX_WAIT_MS } = {}
) => {
  let current = assertJob(job);
  let waited = 0;

  while (ACTIVE_STATUSES.includes(current.status)) {
    if (isCancelled()) return null;
    if (waited >= maxWaitMs) {
      throw new ReportError(MESSAGES.TIMED_OUT, { code: 'TIMED_OUT', requestId: current.request_id || null });
    }
    await sleep(intervalMs);
    waited += intervalMs;
    if (isCancelled()) return null;
    current = await getReportJob(current.job_id);
    if (onUpdate) onUpdate(current);
  }

  if (current.status === 'failed') throw failedJobError(current);
  return current;
};

// Save the generated workbook to the user's device.
export const downloadReport = async (report) => {
  try {
    const blob = await apiDownload(`/api/reports/${encodeURIComponent(report.id)}/download`);
    downloadBlob(report.file_name || 'report.xlsx', blob);
  } catch (err) {
    throw toReportError(err);
  }
};

// Text for the error panel: one line per problem, plus what to do next.
export const describeReportError = (error) => {
  const reportError = toReportError(error);
  const details = reportError.errors.length > 1 || USE_SERVICE_MESSAGE.includes(reportError.code)
    ? reportError.errors.map(messageFor)
    : [];
  const messages = details.length > 0 ? Array.from(new Set(details)) : [reportError.message];

  let hint = '';
  if (reportError.code === 'RATE_LIMITED' || reportError.code === 'BUSY') {
    hint = reportError.retryAfter
      ? `Try again in about ${reportError.retryAfter} second${reportError.retryAfter === 1 ? '' : 's'}.`
      : 'Try again in a moment.';
  } else if (reportError.status === 422 || reportError.status === 415 || reportError.status === 413) {
    hint = 'Fix or remove the file, then generate the report again.';
  }

  return { messages, hint, requestId: reportError.requestId, code: reportError.code };
};
